import { ImgForPath } from "../../../../lib/images.jsx";
import type { ProfileImageData } from "../../../../api/user";
import styles from "./Avatar.css";

type User = {
  name?: string;
  profileImageData?: ProfileImageData | null;
};

type AvatarProps = {
  user: User;
  size?: "lg" | "md" | "sm" | "xs";
  shape?: "circle" | "rounded-square";
  highlight?: boolean;
};

function getInitials(name: string | undefined) {
  return (name?.[0] || "?").toUpperCase();
}

/**
 * Figma names Radix's Avatar for this component, but Radix's Image/Fallback
 * pair only detects a plain `<img src>` failing to load — it doesn't know
 * about `ImgForPath`'s own async S3 lookup, which already owns loading and
 * error states (Suspense + ErrorBoundary) and degrades to a placeholder on
 * its own. Composing the two would fight over the same job, so this decides
 * image-vs-initials itself, the same way the legacy Avatar does.
 */
const Avatar = ({
  user,
  size = "lg",
  shape = "circle",
  highlight,
}: AvatarProps) => {
  const path = user.profileImageData?.path;

  if (path) {
    return (
      <ImgForPath
        path={path}
        alt=""
        className={styles.image({ size, shape })}
      />
    );
  }

  return (
    <div className={styles.fallback({ shape, size, highlight })}>
      {getInitials(user.name)}
    </div>
  );
};

export default Avatar;
